"use server";

import { z } from "zod";
import { prisma } from "@/lib/prisma";
import bcrypt from "bcryptjs";
import { redirect } from "next/navigation";
import { cookies, headers } from "next/headers";
import { AffiliateService } from "@/services/affiliate.service";
import { SubscriptionCompositionService } from "@/services/subscription-composition.service";
import { canonicalE164 } from "@/lib/phone";
import { enderecoParaRegistro } from "@/lib/origem-da-requisicao";
import { permissoesParaCargo } from "@/domain/platform-team/policies/escopo-de-permissao";
// import { v4 as uuidv4 } from 'uuid'; // Removed to use native crypto

/** Lê o código de indicação: hidden input do form com fallback no cookie do middleware. */
async function readAffiliateRef(formData: FormData): Promise<{ refCode: string | null; signupIp: string | null }> {
    try {
        const formRef = formData.get("ref");
        const cookieStore = await cookies();
        const cookieRef = cookieStore.get("mulply_ref")?.value;
        const raw = (typeof formRef === "string" && formRef.trim()) || cookieRef || null;
        const refCode = raw && /^[A-Z0-9]{4,12}$/i.test(raw) ? raw.toUpperCase() : null;

        // ⚠️ MUDANÇA DE FORMATO DECLARADA (2026-08-30). `affiliate.service.ts` levanta
        // `DUPLICATE_IP` comparando este valor com as linhas HISTÓRICAS da mesma conta, e elas
        // guardam o PRIMEIRO item do xff. A comparação novo-contra-antigo deixa de casar.
        // Aceito de propósito: o valor antigo era 100% escolhido pelo cadastrante, então a flag
        // já era contornável trocando o header a cada cadastro. A partir daqui ela passa a
        // valer de verdade — para os cadastros novos entre si.
        const signupIp = enderecoParaRegistro(await headers());

        return { refCode, signupIp };
    } catch {
        return { refCode: null, signupIp: null };
    }
}

/** Apaga o cookie de indicação após atribuição (evita re-atribuição fantasma). */
async function clearAffiliateRefCookie(): Promise<void> {
    try {
        const cookieStore = await cookies();
        cookieStore.delete("mulply_ref");
    } catch { /* best-effort */ }
}

const registerSchema = z.object({
    name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
    email: z.string().email("Email inválido"),
    storeName: z.string().min(3, "Nome da loja deve ter pelo menos 3 caracteres"),
    password: z.string().min(8, "Senha deve ter pelo menos 8 caracteres")
        .regex(/[A-Z]/, "Deve conter letra maiúscula")
        .regex(/[a-z]/, "Deve conter letra minúscula")
        .regex(/[0-9]/, "Deve conter número")
        .regex(/[!@#$%^&*(),.?":{}|<>]/, "Deve conter caractere especial"),
    phone: z.string().optional(),
    confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
    message: "Senhas não conferem",
    path: ["confirmPassword"],
});

export type RegisterState = {
    message?: string;
    errors?: {
        name?: string[];
        email?: string[];
        storeName?: string[];
        password?: string[];
        confirmPassword?: string[];
    };
    success?: boolean;
} | null; // Allow null

export async function registerStoreOwner(prevState: RegisterState, formData: FormData): Promise<RegisterState> {
    const rawData = {
        name: formData.get("name"),
        email: formData.get("email"),
        storeName: formData.get("storeName"),
        password: formData.get("password"),
        confirmPassword: formData.get("confirmPassword"),
        phone: formData.get("phone") || undefined,
    };

    const validatedFields = registerSchema.safeParse(rawData);

    if (!validatedFields.success) {
        return {
            message: "Por favor, corrija os erros abaixo.",
            errors: validatedFields.error.flatten().fieldErrors,
        };
    }

    const { name, email, password, storeName, phone } = validatedFields.data;
    const normalizedPhone = phone?.trim() ? canonicalE164(phone.trim()) : null;

    // Indicação de afiliado (programa de indicações) — nunca quebra o cadastro
    const { refCode, signupIp } = await readAffiliateRef(formData);
    let referralAttached = false;

    try {
        // 1. Check if email already exists
        const existingUser = await prisma.user.findUnique({ where: { email } });
        if (existingUser) {
            return {
                message: "Este email já está cadastrado.",
                errors: { email: ["Email já em uso"] }
            };
        }

        // 2. Create Store Slug
        let slug = storeName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        const existingStore = await prisma.store.findUnique({ where: { slug } });
        if (existingStore) {
            slug = `${slug}-${crypto.randomUUID().slice(0, 4)}`;
        }

        // 3. Hash Password
        const hashedPassword = await bcrypt.hash(password, 12);

        // 9b: pacote PADRÃO (Package.isDefault) p/ trial SEM cartão. Catálogo read-only → fora da tx;
        // null (sem pacote default OU erro) → cadastro cai no PLANO legado (comportamento atual intacto).
        const defaultComposition = await SubscriptionCompositionService.resolveDefaultPackageGrant();

        // 4. Transaction: Create User, Store, Link, Role, and Subscription
        await prisma.$transaction(async (tx) => {
            // Create User
            const user = await tx.user.create({
                data: {
                    name,
                    email,
                    phone: normalizedPhone,
                    password: hashedPassword,
                    role: 'USER',
                    isActive: true,
                    oauthProvider: 'CREDENTIALS',
                }
            });

            // Create Store
            const store = await tx.store.create({
                data: {
                    name: storeName,
                    slug,
                    niche: 'Varejo',
                    timezone: 'America/Sao_Paulo',
                    contactEmail: email,
                }
            });

            // Indicação de afiliado: vincula referral + dias extras de trial.
            // attachReferral nunca lança — atribuição jamais quebra o cadastro.
            let extraTrialDays = 0;
            if (refCode) {
                const attribution = await AffiliateService.attachReferral(tx, {
                    code: refCode,
                    referredStoreId: store.id,
                    email,
                    ip: signupIp,
                });
                extraTrialDays = attribution.extraTrialDays;
                referralAttached = attribution.referral !== null;
            }

            // Create Initial Subscription — Configurable Trial
            // 9b: com PACOTE padrão, a conta nasce MODULAR (planId null) com os módulos do pacote ATIVOS
            // como trial SEM pagamento; senão, PLANO legado default (cascata de fallback — intacto).
            const trialStart = new Date();
            const trialEnd = new Date();

            if (defaultComposition) {
                const trialDays = (defaultComposition.trialDays ?? 0) + extraTrialDays;
                if (trialDays > 0) trialEnd.setDate(trialEnd.getDate() + trialDays);
                const subscription = await tx.subscription.create({
                    data: {
                        storeId: store.id,
                        packageId: defaultComposition.packageId,
                        billingInterval: defaultComposition.interval,
                        status: trialDays > 0 ? "TRIALING" : "ACTIVE",
                        trialEndsAt: trialDays > 0 ? trialEnd : null,
                        currentPeriodStart: trialStart,
                        currentPeriodEnd: trialDays > 0 ? trialEnd : new Date(trialStart.getTime() + 30 * 24 * 60 * 60 * 1000),
                    }
                });
                // Materializa os módulos do pacote como SubscriptionModule ATIVOS (sem gateway/pagamento).
                await SubscriptionCompositionService.reconcileModules(tx, subscription.id, defaultComposition.items);
            } else {
                let defaultPlan = await tx.plan.findFirst({ where: { isDefault: true } });
                if (!defaultPlan) defaultPlan = await tx.plan.findFirst({ where: { name: 'Enterprise' } });
                if (!defaultPlan) defaultPlan = await tx.plan.findFirst({ orderBy: { basePrice: 'desc' } });
                if (!defaultPlan) throw new Error("No default plan found even after fallback");

                const trialDays = (defaultPlan.trialDays ?? 0) + extraTrialDays;
                if (trialDays > 0) trialEnd.setDate(trialEnd.getDate() + trialDays);
                await tx.subscription.create({
                    data: {
                        storeId: store.id,
                        planId: defaultPlan.id,
                        status: trialDays > 0 ? "TRIALING" : "ACTIVE",
                        trialEndsAt: trialDays > 0 ? trialEnd : null,
                        currentPeriodStart: trialStart,
                        currentPeriodEnd: trialDays > 0 ? trialEnd : new Date(trialStart.getTime() + 30 * 24 * 60 * 60 * 1000), // Default 30 days if no trial
                    }
                });
            }

            // Link User to Store
            await tx.userStore.create({
                data: {
                    userId: user.id,
                    storeId: store.id
                }
            });

            // Create specific Admin role for this store
            const adminRole = await tx.role.create({
                data: {
                    name: 'Administrador',
                    slug: 'admin',
                    description: 'Acesso total à loja',
                    storeId: store.id
                }
            });

            await tx.userRoleAssign.create({
                data: {
                    userId: user.id,
                    roleId: adminRole.id
                }
            });

            // Só permissões de LOJA no cargo da loja — antes "todas", inclusive `platform.*` ([R120]).
            const allPermissions = permissoesParaCargo(adminRole, await tx.permission.findMany());
            if (allPermissions.length > 0) {
                await tx.rolePermission.createMany({
                    data: allPermissions.map(p => ({
                        roleId: adminRole.id,
                        permissionId: p.id
                    }))
                });
            }
        });

        if (referralAttached) {
            await clearAffiliateRefCookie();
        }

        return { success: true };

    } catch (error) {
        console.error("Registration error:", error);
        return { message: "Erro ao criar conta. Tente novamente." };
    }
}
